import React, { MouseEvent } from 'react';
import Breadcrumb from '../../../Breadcrumb';
import CustomEditorItemComponent from '../../../custom/CustomEditorItemComponent';
import CustomTabv2 from '../../../custom/CustomTabv2';
import { FormicaProps, withFormica } from '../../../../helpers/withFormica';
import { RootState } from '../../../../redux/store';
import { ComponentState, enterComponent, leaveComponent } from '../../../../redux/actions/ComponentActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import ValueFactory from '../../../../util/ValueFactory';
import { CaseSettings } from '../../../../domain/case/CaseDomains';
import ServiceContainer from '../../../../config/ServiceContainer';
import { ServiceNames } from '../../../../constant/ServiceNames';
import DomainConfigurationService from '../../../../service/DomainConfigurationService';
import ComponentName from '../../../../constant/ComponentName';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../../constant/CollectionNames';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import { customConfirm } from '../../../custom/CustomPopupConfirm';
import DomainConfigurationGeneralComponent from './general/DomainConfigurationGeneralComponent';
import { TransactionJsonModel } from '../../../../domain/stream/transaction/TransactionDomains';
import { List } from '../../../../domain/list/ListDomains';
import DomainConfigurationPanelComponent from './panel/DomainConfigurationPanelComponent';
import DomainConfigurationAccessComponent from './access/DomainConfigurationAccessComponent';
import {
  changeActions,
  changeConfiguration,
  changeConfigurationDirty,
  changeConfigurationIsUpdate,
  changeEventItems,
  changeLists,
  clearConfiguration,
} from '../../../../redux/actions/ConfigurationActions';
import RoleHelper from '../../../../RoleHelper';
import MessageUtil from '../../../../util/MessageUtil';
import ComponentAuditLog from '../../../ComponentAuditLog';
import { changePageFilter, clearPage } from '../../../../redux/actions/PageActions';
import { ActionJsonModel } from '../../../../domain/collection/rule/action/ActionDomains';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../../../helpers/StandardEditorContainerComponent';
import FilterUtil from '../../../../util/FilterUtil';
import { ConfigurationState } from '../../../../redux/reducers/ConfigurationReducer';
import DomainGraphContainer from './graph/DomainGraphContainer';
import { EnrichmentBase } from '../../../../domain/enrichment/EnrichmentDomain';
import { isStringValid } from '../../../../util/Generator';
import LanguageUtil from '../../../../util/LanguageUtil';
import CustomLoading from '../../../custom/CustomLoading';
import DomainConfigurationSuspiciousActivityReportComponent from './sar/DomainConfigurationSuspiciousActivityReportComponent';
import { ConfirmPopup } from 'primereact/confirmpopup';
import DomainConfigurationReasonComponent from './reason/DomainConfigurationReasonComponent';
import DomainConfigurationActionComponent from './action/DomainConfigurationActionComponent';
import DomainConfigurationEntityComponent from './entity/DomainConfigurationEntityComponent';
import CaseActionUtil from '../../../../util/CaseActionUtil';
import eq = query.eq;
import empty = query.empty;
import QueryBuilder = query.QueryBuilder;
import and = query.and;

class DomainConfigurationComponent extends StandardEditorContainerComponent<Props, State, CaseSettings> {
  public state: State = new State();

  private computation: subscribe.Computation | undefined;
  private domainConfigurationSubscription: subscribe.Subscription | undefined;
  private eventSubscription: subscribe.Subscription | undefined;
  private listSubscription: subscribe.Subscription | undefined;
  private actionSubscription: subscribe.Subscription | undefined;
  private userSubscription: subscribe.Subscription | undefined;
  private enrichmentSubscription: subscribe.Subscription | undefined;

  private domainConfigurationService: DomainConfigurationService = ServiceContainer.getInstance().get(ServiceNames.DOMAIN_CONFIGURATION);
  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.CONFIGURATION,
      icon: 'settings',
    };
    super(props, args);
  }

  public render() {
    const {
      configuration: { configuration, isUpdate },
    } = this.props;
    const { loading } = this.state;

    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col h-full w-full overflow-y-hidden'}>
        <Breadcrumb />
        <ConfirmPopup />
        <CustomEditorItemComponent
          name={configuration.name}
          description={configuration.description!}
          component={ComponentName.CONFIGURATION}
          onSave={this.onSave}
          onRemove={this.onRemove}
          status={'Empty'}
          isUpdate={isUpdate}
          statusVisible={false}
          icon={'settings'}
          onChangeDescription={this.onChangeDescription}
          onChangeName={this.onChangeName}
          disableSave={!RoleHelper.canEdit('CaseConfiguration')}
          disableRemove={!RoleHelper.canEdit('CaseConfiguration')}
          disableNameChange={!RoleHelper.canEdit('CaseConfiguration')}
          disableDescriptionChange={!RoleHelper.canEdit('CaseConfiguration')}
          disableDeploy={!RoleHelper.canDeploy('CaseConfiguration')}
          disableUndeploy={!RoleHelper.canDeploy('CaseConfiguration')}
        />
        <div className={'flex flex-col overflow-y-scroll h-full w-full bg-white rounded'}>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentWillUnmount() {
    const { clearConfiguration } = this.props;
    clearConfiguration();
    await super.componentWillUnmount();
  }

  protected _startTracker = () => {
    const listQuery = query.QueryBuilder.select().from(CollectionNames.LISTS).where(eq('type', 'INTERNAL_LIST')).build();
    const domainQuery = query.QueryBuilder.select().from(CollectionNames.CASE_SETTINGS).build();
    const eventQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(empty()).build();
    const actionQuery = QueryBuilder.select().from(CollectionNames.ACTIONS).where(eq('type', 'transaction')).select('template', 'transaction', 'id').build();
    const userQuery = query.QueryBuilder.select()
      .from(CollectionNames.USERS)
      .where(empty())
      .select('username', 'firstName', 'lastName', 'id', 'riskType')
      .build();
    const enrichmentQuery = query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).where(empty()).build();

    this.eventSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, eventQuery);
    this.listSubscription = subscribe.Formica.subscribe(CollectionNames.LISTS, listQuery);
    this.domainConfigurationSubscription = subscribe.Formica.subscribe(CollectionNames.CASE_SETTINGS, domainQuery);
    this.actionSubscription = subscribe.Formica.subscribe(CollectionNames.ACTIONS, actionQuery);
    this.userSubscription = subscribe.Formica.subscribe(CollectionNames.USERS, userQuery);
    this.enrichmentSubscription = subscribe.Formica.subscribe(CollectionNames.ENRICHMENTS, enrichmentQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [
      this.domainConfigurationSubscription,
      this.eventSubscription,
      this.listSubscription,
      this.actionSubscription,
      this.userSubscription,
      this.enrichmentSubscription,
    ]);
  };

  protected _stopTracker = async () => {
    await this.domainConfigurationSubscription?.stop();
    await this.eventSubscription?.stop();
    await this.listSubscription?.stop();
    await this.actionSubscription?.stop();
    await this.userSubscription?.stop();
    await this.enrichmentSubscription?.stop();
    this.computation?.stop();
  };

  protected tracker = async () => {
    const { changeLists, changeActions, changeEventItems, changeConfigurationDirty } = this.props;
    if (
      this.eventSubscription?.ready() &&
      this.domainConfigurationSubscription?.ready() &&
      this.listSubscription?.ready() &&
      this.actionSubscription?.ready()
    ) {
      const domainId = this.getEventFromRoute();
      const listQuery = query.QueryBuilder.select().from(CollectionNames.LISTS).build();
      const lists: string[] = (await this.repository.findMany<List>(listQuery)).flatMap((l) => l.name);
      changeLists(lists);
      const eventQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
      const eventItems: TransactionJsonModel[] = await this.repository.findMany<TransactionJsonModel>(eventQuery);
      const domainQuery = query.QueryBuilder.select().from(CollectionNames.CASE_SETTINGS).build();
      const domainsRaw: CaseSettings[] = await this.repository.findMany(domainQuery);
      const domains = domainsRaw.map(d => CaseActionUtil.normalizeCaseSettingsActions(d) as CaseSettings);
      this.setState({ domains });
      const domainRaw = domains.find((domain) => domain.name == domainId) || ValueFactory.initializeDomainConfiguration();
      const domain = CaseActionUtil.normalizeCaseSettingsActions(domainRaw) as CaseSettings;
      if (domainId && domainId !== 'create') {
        RoleHelper.setCurrentAccess(domain);
      }
      const usedTransactions: string[] = domains.flatMap((domain) => domain.dataSource);
      const events: TransactionJsonModel[] = eventItems.filter((event) => !usedTransactions.includes(event.name) || domain.dataSource == event.name);
      changeEventItems(events);

      const enrichmentQuery = query.QueryBuilder.select()
        .from(CollectionNames.ENRICHMENTS)
        .where(and(eq('stage', 'ACTIVE'), eq('transaction', domain.dataSource)))
        .select('id')
        .build();
      const enrichmentData: EnrichmentBase[] = await this.repository.findMany(enrichmentQuery);
      const enrichmentNames: string[] = (enrichmentData.map((enrichment) => enrichment.id) as string[]) || [];
      this.setState({ enrichmentNames });

      const actionQuery = QueryBuilder.select().from(CollectionNames.ACTIONS).select('template', 'transaction', 'id');
      if (domainId && domain.dataSource != '') actionQuery.where(eq('transaction', domain.dataSource));
      const actions = await this.repository.findMany<ActionJsonModel>(actionQuery.build());
      changeActions(actions);
      if (domainId == undefined || domainId === 'create') {
        this.changeIsUpdate(false);
        this.setState({ loading: false });
        changeConfigurationDirty(false);
        return;
      }
      this.changeItem(domain);
      this.changeIsUpdate(true);
      await this.getFilterFields();
      this.setState({ loading: false });
      changeConfigurationDirty(false);
    }
  };

  private getFilterFields = async (saveFilter: boolean = true) => {
    const {
      configuration: { configuration },
    } = this.props;
    await FilterUtil.getCaseFields(configuration.dataSource, saveFilter);
  };

  private getEventFromRoute = () => this.props.router.params.id;

  private onSave = async () => {
    const {
      configuration: { configuration, isUpdate },
      lastCategory,
    } = this.props;
    const validationError = this.validateConfiguration(configuration);
    if (validationError) {
      MessageUtil.error({ detail: validationError });
      return;
    }

    try {
      const isSaved = await this.domainConfigurationService.saveDomainConfiguration(configuration, isUpdate, lastCategory);
      if (isSaved) {
        this.changeIsUpdate(true);
        if (!isUpdate) await this.switchToUpdate();
      }
    } catch (e) {
      console.error(e);
    }
  };

  private validateConfiguration = (configuration: CaseSettings): string | null => {
    if (!configuration.name) {
      return LanguageUtil.get({ id: 'pages.configuration.editor.nameError', defaultMessage: 'Configuration name cannot be empty.' });
    }

    if (!configuration.keyField) {
      return LanguageUtil.get({ id: 'pages.configuration.editor.error', defaultMessage: 'Datasource and key fields cannot be empty.' });
    }

    if (configuration.assignmentCandidatesRequired && configuration.assignmentCandidates.length === 0) {
      return LanguageUtil.get({ id: 'pages.configuration.editor.autoAssignUsersError', defaultMessage: 'Auto Assign is enabled. Please select at least one user.' });
    }

    return null;
  };

  private onRemove = async (e: MouseEvent<HTMLButtonElement>) => {
    const {
      configuration: { configuration },
    } = this.props;
    const {
      router: { navigate },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        const success = await this.domainConfigurationService.removeDomainConfiguration(configuration.name);
        if (success) {
          navigate(`/configuration`);
        }
      },
      message: LanguageUtil.get({ id: 'messages.confirm.removeConfig', defaultMessage: 'Are you sure to remove this configuration?' }),
    });
  };

  private changeIsUpdate = (value: boolean) => {
    const { changeConfigurationIsUpdate } = this.props;
    changeConfigurationIsUpdate(value);
  };

  private switchToUpdate = async () => {
    const {
      router: { navigate, params },
      enterComponent,
    } = this.props;
    const {
      configuration: { configuration },
    } = this.props;
    if (configuration.name == '') return;
    enterComponent({ component: ComponentName.CONFIGURATION, id: configuration.name });
    navigate(`/configuration${params.folderId ? `/folder/${params.folderId}` : ''}/edit/${configuration.name}`, { replace: true });
    this.computation?.onStop(() => this.startTracker());
    await this.stopTracker();
  };

  private onChangeDescription = (description: string) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    configuration.description = description;
    changeConfiguration(configuration);
  };

  private onChangeName = (name: string) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    if (isStringValid(name)) {
      configuration.name = name.trim();
      changeConfiguration(configuration);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  protected getTabs = () => {
    const { enrichmentNames, domains } = this.state;
    const {
      configuration: { configuration },
      component: { module },
      user,
    } = this.props;
    const tabs = [
      {
        key: 'general',
        label: LanguageUtil.get({ id: 'common.tabs.general', defaultMessage: 'General' }),
        content: <DomainConfigurationGeneralComponent />,
        testid: `${ComponentName.CONFIGURATION}_general_tab`,
      },
      {
        key: 'panel',
        label: LanguageUtil.get({ id: 'common.tabs.panel', defaultMessage: 'Panel' }),
        content: <DomainConfigurationPanelComponent enrichmentNames={enrichmentNames} />,
        testid: `${ComponentName.CONFIGURATION}_panel_tab`,
      },
      {
        key: 'reason',
        label: LanguageUtil.get({ id: 'common.tabs.reason', defaultMessage: 'Reason' }),
        content: <DomainConfigurationReasonComponent />,
        testid: `${ComponentName.CONFIGURATION}_reason_tab`,
      },
      {
        key: 'action',
        label: LanguageUtil.get({ id: 'common.tabs.action', defaultMessage: 'Action' }),
        content: <DomainConfigurationActionComponent />,
        testid: `${ComponentName.CONFIGURATION}_action_tab`,
      },
      {
        key: 'graph',
        label: LanguageUtil.get({ id: 'common.tabs.graph', defaultMessage: 'graph' }),
        content: <DomainGraphContainer />,
        testid: `${ComponentName.CONFIGURATION}_graph_tab`,
      },
      {
        key: 'entity',
        label: LanguageUtil.get({ id: 'common.tabs.entity', defaultMessage: 'Entity' }),
        content: <DomainConfigurationEntityComponent domains={domains} />,
        testid: `${ComponentName.CONFIGURATION}_entity_tab`,
      },
      {
        key: 'access',
        label: LanguageUtil.get({ id: 'common.tabs.access', defaultMessage: 'Access' }),
        content: <DomainConfigurationAccessComponent />,
        visible: RoleHelper.hasGlobalMenuEdit('CaseConfiguration', true),
        testid: `${ComponentName.CONFIGURATION}_access_tab`,
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: 'common.tabs.audit', defaultMessage: 'Audit' }),
        content: <ComponentAuditLog component={ComponentName.CONFIGURATION} documentName={configuration.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.CONFIGURATION, true) && this.environmentUtil.isAuditLogVisible(),
        testid: `${ComponentName.CONFIGURATION}_audit_tab`,
      },
    ];
    if (user.activeRiskType == 'compliance' && module.includes('aml')) {
      tabs.splice(3, 0, {
        key: 'sar',
        label: LanguageUtil.get({ id: 'common.tabs.sar', defaultMessage: 'SAR' }),
        content: <DomainConfigurationSuspiciousActivityReportComponent />,
        testid: `${ComponentName.CONFIGURATION}_sar_tab`,
      });
    }
    return tabs;
  };

  protected changeItem = (item: CaseSettings) => {
    const { changeConfiguration } = this.props;
    changeConfiguration(item);
  };
}

interface Props extends FormicaProps, ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  lastCategory: state.page.lastCategory,
  page: state.page,
  filter: state.page.filter,
  configuration: state.configuration as ConfigurationState,
  user: state.auth.user,
  component: state.component as ComponentState,
});
const mapDispatchToProps = {
  enterComponent,
  leaveComponent,
  changeConfiguration,
  changeActions,
  changeLists,
  changeConfigurationIsUpdate,
  changeEventItems,
  clearPage,
  clearConfiguration,
  changePageFilter,
  changeConfigurationDirty,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  loading: boolean = true;
  enrichmentNames: string[] = [];
  domains: CaseSettings[] = [];
}

export default withFormica(connector(DomainConfigurationComponent));
